import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import type { Policy } from '../../services/policiesApi';
import type { PolicyTroubleshootingView } from '../../utils/policyTroubleshooting';
import type { PresentedPolicy } from '../../domain/policies/policyPresentation';
import { CONFIG_SCOPE_LABELS } from '../../utils/configVocabulary';
import PolicyStatusBadge from './PolicyStatusBadge';

export interface PolicyTableRowModel {
  id: string;
  policy: Policy;
  view: PolicyTroubleshootingView;
  presented: PresentedPolicy | null;
  displayName: string;
  secondaryId: string;
  packLabel: string;
  targetLabel: string;
  targetDeviceLabel: string;
  targetCameraLabel: string;
  scopeLabel: string;
  statusLabel: string;
  deploymentLabel: string;
  lastUpdatedLabel: string;
  scopeIndicator?: string;
}

export interface PolicyTableProps {
  rows: PolicyTableRowModel[];
  loading?: boolean;
  selectedPolicyId?: string | null;
  onSelectRow: (row: PolicyTableRowModel) => void;
  onOpenActions: (event: React.MouseEvent<HTMLElement>, row: PolicyTableRowModel) => void;
}

const nameCell = (row: PolicyTableRowModel) => (
  <Box sx={{ minWidth: 0 }}>
    <Tooltip title={row.displayName}>
      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
        {row.displayName}
      </Typography>
    </Tooltip>
    {row.secondaryId ? (
      <Tooltip title={row.secondaryId}>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 280 }}>
          {row.secondaryId}
        </Typography>
      </Tooltip>
    ) : null}
  </Box>
);

const targetCell = (row: PolicyTableRowModel) => (
  <Box sx={{ minWidth: 0 }}>
    <Tooltip title={row.targetDeviceLabel || CONFIG_SCOPE_LABELS.noEdgeDevice}>
      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
        {row.targetDeviceLabel || CONFIG_SCOPE_LABELS.noEdgeDevice}
      </Typography>
    </Tooltip>
    <Tooltip title={row.targetCameraLabel || CONFIG_SCOPE_LABELS.noCameraStream}>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>
        {row.targetCameraLabel || CONFIG_SCOPE_LABELS.noCameraStream}
      </Typography>
    </Tooltip>
  </Box>
);

const PolicyTable: React.FC<PolicyTableProps> = ({
  rows,
  loading = false,
  selectedPolicyId = null,
  onSelectRow,
  onOpenActions,
}) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));

  if (compact) {
    return (
      <Stack spacing={1.25}>
        {rows.map((row) => {
          const selected = selectedPolicyId === row.id;
          return (
            <Paper
              key={row.id}
              variant="outlined"
              onClick={() => onSelectRow(row)}
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                borderColor: selected ? 'primary.main' : 'divider',
                boxShadow: selected ? '0 0 0 1px rgba(25, 118, 210, 0.15)' : 'none',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {nameCell(row)}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {row.packLabel}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                    <PolicyStatusBadge label={row.statusLabel} />
                    <PolicyStatusBadge label={row.deploymentLabel} kind="deployment" />
                    {row.scopeIndicator ? <PolicyStatusBadge label={row.scopeIndicator} kind="scope" /> : null}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  aria-label="Policy actions"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenActions(event, row);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  mt: 1.25,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">
                    {CONFIG_SCOPE_LABELS.edgeDevice} / {CONFIG_SCOPE_LABELS.cameraStream}
                  </Typography>
                  {targetCell(row)}
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">
                    Scope
                  </Typography>
                  <Typography variant="body2">{row.scopeLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Deployment
                  </Typography>
                  <Typography variant="body2">{row.deploymentLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last updated
                  </Typography>
                  <Typography variant="body2">{row.lastUpdatedLabel}</Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
        {!loading && rows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No policies match the current filters.
            </Typography>
          </Paper>
        ) : null}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              '& .MuiTableCell-root': {
                bgcolor: '#faf8f4',
                borderBottom: '1px solid #e5e7eb',
                color: '#475467',
                fontWeight: 700,
              },
            }}
          >
            <TableCell>Policy</TableCell>
            <TableCell>Pack</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Deployment</TableCell>
            <TableCell>Last Updated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const selected = selectedPolicyId === row.id;
            return (
              <TableRow
                key={row.id}
                hover
                onClick={() => onSelectRow(row)}
                sx={{
                  cursor: 'pointer',
                  '& .MuiTableCell-root': { borderBottom: '1px solid #eef1f4' },
                  ...(selected
                    ? {
                        bgcolor: 'rgba(29, 79, 145, 0.07)',
                        boxShadow: 'inset 3px 0 0 #1d4f91',
                      }
                    : null),
                }}
              >
                <TableCell sx={{ maxWidth: 320 }}>{nameCell(row)}</TableCell>
                <TableCell>{row.packLabel}</TableCell>
                <TableCell>{targetCell(row)}</TableCell>
                <TableCell>
                  <Tooltip title={row.scopeLabel}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                      {row.scopeLabel}
                    </Typography>
                  </Tooltip>
                  {row.scopeIndicator ? (
                    <Box sx={{ mt: 0.5 }}>
                      <PolicyStatusBadge label={row.scopeIndicator} kind="scope" />
                    </Box>
                  ) : null}
                </TableCell>
                <TableCell>
                  <PolicyStatusBadge label={row.statusLabel} />
                </TableCell>
                <TableCell>
                  <PolicyStatusBadge label={row.deploymentLabel} kind="deployment" />
                </TableCell>
                <TableCell>{row.lastUpdatedLabel}</TableCell>
                <TableCell align="right">
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelectRow(row);
                      }}
                    >
                      View
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="Policy actions"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenActions(event, row);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary">
                  No policies match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PolicyTable;
